<template>
	<a :id="id" :href="href" class="StatsIcon" :class="classes" :aria-current="current">
		<span class="StatsIcon-stat">
			<span v-if="icon" class="StatsIcon-stat-icon">
				<CloIcon :symbol="icon" />
			</span>
			<span class="StatsIcon-stat-count">
				{{ stat }}
				<span v-if="loading" class="LoadingAnimation">
					<CloIcon :symbol="icon" />
				</span>
			</span>
		</span>
		<span class="StatsIcon-label">{{ label }}</span>
		<span v-if="secondary" class="StatsIcon-secondary">{{ secondary }} {{ secondaryLabel }}</span>
		<span v-if="status" class="StatsIcon-status">
			<span>{{ status }}</span>
		</span>
	</a>
</template>

<script>
import Vue from 'vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation';
import CloIcon from '@/icons/CloIcon/CloIcon';

export default Vue.extend({
	name: 'CloStat',
	components: {
		CloLoadingAnimation,
		CloIcon,
	},
	data() {
		return {};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
		icon: {
			type: String,
			default: null,
		},
		stat: {
			type: String,
			default: '',
		},
		label: {
			type: String,
			default: '',
		},
		secondary: {
			type: String,
			default: '',
		},
		secondaryLabel: {
			type: String,
			default: '',
		},
		current: {
			type: Boolean,
			default: false,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		status: {
			type: String,
			default: '',
		},
		href: {
			type: String,
			default: '#',
		},
		tab: {
			type: Boolean,
			default: false,
		},
		reverse: {
			type: Boolean,
			default: false,
		},
		muted: {
			type: Boolean,
			default: false,
		},
		vertical: {
			type: Boolean,
			default: false,
		},
		inline: {
			type: Boolean,
			default: false,
		},
		labelAbove: {
			type: Boolean,
			default: false,
		},
		size: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes() {
			// TODO: deprecate selected and highlight - use current instead?
			const classes = {
				'StatsIcon--tab': this.tab,
				'StatsIcon--reverse': this.reverse,
				'StatsIcon--muted': this.muted,
				'StatsIcon--vertical': this.vertical,
				'StatsIcon--inline': this.inline,
				'StatsIcon--labelAbove': this.labelAbove,
			};
			if (this.size) {
				classes['StatsIcon--' + this.size] = true;
			}
			return classes;
		},
	},
});
</script>

<style lang="scss">
@use "sass:color";

/* --------------------------------
	StatsIcon styles
-------------------------------- */

/* Main wrapper for component */
.StatsIcon {
	color: $color-text-muted;
	display: inline-flex;
	flex-direction: column;
}

a .StatsIcon {
	@include inset-md;
	color: $color-link;
	background-color: $color-primary-0;
	border-radius: 3px;
}

a:active .StatsIcon {
	color: $color-white;
	background-color: $color-link-special;
}

a.StatsIcon {
	color: $color-link;
	&:hover,
	&:focus,
	&:active {
		color: color.adjust($color-link, $lightness: -5%);
	}
	&[aria-current='true'] {
		color: $color-link-special;
	}
}

/* Wrapper for stat icon and count */
.StatsIcon-stat {
	display: inline-flex;
	align-items: center;
}

/* Stat icon */
.StatsIcon-stat-icon {
	display: flex;
	align-items: center;
	@include inline-sm;
	.Icon {
		@include icon-md;
	}
}

/* Stat count */
.StatsIcon-stat-count {
	//@include heading;
	@include text(6);
	margin-bottom: 0;
	font-weight: bold;
	/* Small variant for long numbers. Add with JS. */
	&--sm {
		@include text(5);
	}
	&--xs {
		@include text(4);
	}
	/* Loading animation for asynchronous loading of stat */
	.LoadingAnimation {
		@include inset-xs;
		background-color: transparent;
		display: flex;
		justify-content: center;
		position: relative;
		top: auto;
		left: auto;
		bottom: auto;
		right: auto;
		.Icon {
			display: flex;
			fill: $color-neutral-2;
			margin: 0;
			@include icon-md;
		}
	}
}

/* Label - appears underneath icon and count */
.StatsIcon-label {
	@include text(3);
	&:not(:last-child) {
		@include stack-xxs;
	}
}

/* Secondary - optional text for an additional stat */
.StatsIcon-secondary {
	color: $color-text-muted;
	@include text(2);
	display: none; // hide for small screens
	@include breakpoint(medium) {
		display: unset; // show for medium up screens
	}
	/* Variant to show for all screens, overriding default behavior */
	&--showSmall {
		display: unset;
	}
}

/* Status - optional status text to be added/removed with js */
.StatsIcon-status {
	align-items: baseline;
	color: $color-text-alert;
	display: flex;
	flex-wrap: wrap;
	@include text(2);
	> * {
		@include inline-xs;
	}
	.Button {
		@include text(2);
	}
}

/* ----------------
	Color Variants
---------------- */

/* Reverse variant for dark backgrounds */
.StatsIcon--reverse {
	color: $color-white;
	border-left-color: $color-neutral-3;
	.StatsIcon-secondary {
		color: $color-text-muted-reverse;
	}
	.StatsIcon-status {
		color: $color-text-alert-reverse;
		.Button {
			color: $color-link-reverse;
			&:hover,
			&:focus,
			&:active {
				color: $color-link-hover-reverse;
			}
		}
	}
	&.StatsIcon--highlight {
		color: color.adjust(color.adjust($color-secondary-2, $saturation: 30%), $hue: 0deg);
	}
	&.StatsIcon--muted {
		color: $color-text-muted-reverse;
	}
}

a.StatsIcon--reverse {
	color: $color-link-reverse;
	&:hover,
	&:focus,
	&:active {
		color: $color-link-hover-reverse;
	}
	&.StatsIcon--highlight {
		color: color.adjust(color.adjust($color-secondary-2, $saturation: 30%), $hue: 0deg);
		&:hover,
		&:focus,
		&:active {
			color: color.adjust(color.adjust(color.adjust($color-secondary-2, $saturation: 30%), $hue: 0deg), $lightness: 10%);
		}
	}
	&.StatsIcon--selected {
		color: color.adjust(color.adjust($color-secondary-2, $saturation: 30%), $hue: 0deg);
		&:hover,
		&:focus,
		&:active {
			color: color.adjust(color.adjust($color-secondary-2, $saturation: 30%), $hue: 0deg);
		}
	}
	&.StatsIcon--muted {
		color: $color-text-muted-reverse;
		&:hover,
		&:focus,
		&:active {
			color: color.adjust($color-text-muted-reverse, $lightness: 5%);
		}
	}
}

/* Highlight variant for highlighting a particular stat */
.StatsIcon--highlight {
	color: $color-secondary-3;
	&:link,
	&:visited {
		color: $color-secondary-3;
		&:hover,
		&:active,
		&:focus {
			color: color.adjust($color-secondary-3, $lightness: -5%);
		}
	}
}

/* Muted variant  */
.StatsIcon--muted {
	color: $color-text-muted;
	&:link,
	&:visited {
		color: $color-primary-2;
		&:hover {
			color: color.adjust($color-primary-2, $lightness: -5%);
		}
	}
}

/* Selected variant for showing active status with over color from highlight */
.StatsIcon--selected {
	pointer-events: none;
	color: color.adjust($color-secondary-3, $lightness: -5%);
	&:link,
	&:visited {
		color: color.adjust($color-secondary-3, $lightness: -5%);
		&:hover,
		&:active,
		&:focus {
			color: color.adjust($color-secondary-3, $lightness: -5%);
		}
	}
}

// Tabbed stats with an inset box around them
.StatsIcon--tab {
	@include inset-squish-md;
	border-radius: 3px;
	background-color: $color-neutral-0;
	&.alt {
		background-color: none;
		border: 1px solid color.adjust($color-neutral-3, $alpha: -0.75);
	}
	&.alt2 {
		padding: 0;
		background-color: transparent !important;
	}
}
a.StatsIcon--tab {
	color: $color-link;
	background-color: $color-primary-0;
	border: 1px solid transparent;
	cursor: pointer;
	&.alt {
		background-color: none;
		border-color: color.adjust($color-link, $alpha: -0.75);
		border-color: transparent;
	}
	&:hover {
		color: color.adjust($color-link, $lightness: -5%);
		background-color: color.adjust($color-primary-0, $lightness: -5%);
		border-color: color.adjust($color-link, $alpha: -0.75);
		&.alt {
			border-color: color.adjust($color-link, $lightness: -5%);
		}
	}
	&[aria-current='true'] {
		color: $color-link-special;
		background-color: $color-secondary-0;
		border-color: color.adjust($color-link-special, $alpha: -0.75);
		&.alt {
			color: $color-white;
			background-color: color.adjust($color-link-special, $lightness: -5%);
			background-color: $color-primary-5;
			border-color: none;
			.StatsIcon-secondary {
				color: $color-white;
			}
		}
	}
}

/* ----------------
	Size Variants
---------------- */

// Small variant
@mixin StatsIcon--sm {
	.StatsIcon-stat-icon {
		@include inline-xs;
		.Icon {
			@include icon-sm;
		}
	}
	.StatsIcon-stat-count {
		@include text(5);
		&--sm {
			@include text(4);
		}
		&--xs {
			@include text(3);
		}
		.LoadingAnimation {
			.Icon {
				@include icon-sm;
			}
		}
	}
	.StatsIcon-label {
		@include text(2);
	}
	.StatsIcon-secondary {
		@include text(1);
	}
	.StatsIcon-status {
		@include text(1);
		.Button {
			@include text(1);
		}
	}
}
.StatsIcon--sm {
	@include StatsIcon--sm;
}

// Large variant
@mixin StatsIcon--lg {
	.StatsIcon-stat {
		@include stack-xs;
	}
	.StatsIcon-stat-icon {
		@include inline-sm;
		.Icon {
			@include icon-lg;
		}
	}
	.StatsIcon-stat-count {
		@include text(7);
		&--sm {
			@include text(6);
		}
		&--xs {
			@include text(5);
		}
		.LoadingAnimation {
			.Icon {
				@include icon-lg;
			}
		}
	}
}
.StatsIcon--lg {
	@include StatsIcon--lg;
}

// Responsive variant - small, medium, large
.StatsIcon--responsive {
	@include breakpoint(small) {
		@include StatsIcon--sm;
	}
	@include breakpoint(mediumsmall) {
		@include StatsIcon--sm;
	}
	@include breakpoint(large) {
		@include StatsIcon--lg;
	}
}

// Responsive sm variant - small and medium
.StatsIcon--responsive-sm {
	@include breakpoint(small) {
		@include StatsIcon--sm;
	}
	@include breakpoint(mediumsmall) {
		@include StatsIcon--sm;
	}
}

// Responsive large variant - medium and large
.StatsIcon--responsive-lg {
	@include breakpoint(large) {
		@include StatsIcon--lg;
	}
}

/* ----------------
	Layout Variants
---------------- */

/* Vertical variant wraps and centers stats */
@mixin StatsIcon--vertical {
	text-align: center;
	.StatsIcon-stat {
		flex-direction: column;
		justify-content: center;
		.StatsIcon-stat-icon {
			margin: 0;
		}
	}
}
.StatsIcon--vertical {
	@include StatsIcon--vertical;
}

/* Small vertical wraps and centers stats only on small screens */
.StatsIcon--sm-vertical {
	@include breakpoint(mediumsmall, down) {
		@include StatsIcon--vertical;
	}
}

/* Inline variant makes labels appear inline with stat */
.StatsIcon--inline {
	display: inline-flex;
	flex-direction: row;
	flex-wrap: wrap;
	align-items: baseline;
	.StatsIcon-stat {
		@include inline-sm;
		align-items: baseline;
		.StatsIcon-stat-icon {
			align-self: stretch;
			align-items: center;
		}
	}
	.StatsIcon-label,
	.StatsIcon-secondary {
		margin-right: $space-rel-sm;
		align-items: baseline;
	}
	.StatsIcon-label {
		font-weight: bold;
	}
	// When combined with small
	&.StatsIcon--sm {
		.StatsIcon-stat {
			@include inline-xs;
		}
	}
	// When combined with lg
	&.StatsIcon--lg {
		.StatsIcon-stat {
			@include inline-sm;
		}
		.StatsIcon-label {
			@include text(5);
		}
	}
}

// Label above places label above icon and count
.StatsIcon--labelAbove {
	.StatsIcon-label {
		order: -1;
	}
}
</style>
